import React from "react";
import { useTranslation } from "react-i18next";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import { useMarket } from "@/hooks/useMarket";
import {
  getWhatsAppSupportUrl,
  marketHasAdvisoryCall,
  marketHasImaging,
} from "@/config/market";

interface Expert {
  id: string;
  name: string;
  image: string;
}

const experts: Expert[] = [
  {
    id: "fischer",
    name: "Prof. Dr. med. Michael Fischer",
    image: "/advisory/fischer.webp",
  },
  { id: "erat", name: "Dr. Anna Erat MD, PhD", image: "/advisory/erat.webp" },
  {
    id: "jagoda",
    name: "Dr. Agata Jagoda",
    image: "/advisory/agata.webp",
  },
  {
    id: "komaromi",
    name: "Dr. Hadrien Komaromi",
    image: "/advisory/hadrien.webp",
  },
  {
    id: "wiestler",
    name: "Prof. Dr. Benedikt Wiestler",
    image: "/advisory/wiestler.webp",
  },
  {
    id: "donati",
    name: "Prof. Dr. med. Olivio Donati",
    image: "/advisory/donati.webp",
  },
];

// NL swaps Dr. Agata Jagoda for Dr. Jacob J. Visser (Erasmus MC), keeping the
// panel at six.
const VISSER: Expert = {
  id: "visser",
  name: "Dr. Jacob J. Visser",
  image: "/advisory/visser.webp",
};

interface AdvisoryBoardProps {
  language: BookingLanguage;
}

export const AdvisoryBoard: React.FC<AdvisoryBoardProps> = ({ language }) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  // Markets without imaging (DE, NL) don't offer scans, so drop the "scan"
  // wording there.
  const { market } = useMarket();
  const descriptionKey = marketHasImaging(market)
    ? "advisory.description"
    : "advisory.descriptionNoScan";

  // Markets without a bookable advisory call (NL) point at WhatsApp instead.
  const hasAdvisoryCall = marketHasAdvisoryCall(market);

  // NL swaps Dr. Agata Jagoda for Dr. Jacob J. Visser; other markets keep the
  // default panel.
  const displayedExperts =
    market === "nl"
      ? experts.map((e) => (e.id === "jagoda" ? VISSER : e))
      : experts;

  const handleCtaClick = () => {
    const url = hasAdvisoryCall
      ? t("advisory.bookCallUrl")
      : getWhatsAppSupportUrl(market);
    window.open(url, "_blank");
  };

  return (
    <section className="w-full py-12 md:py-16">
      <div className="max-w-[1120px] mx-auto">
        {/* Header section - two column layout matching FAQ */}
        <div className="flex flex-col md:flex-row md:gap-16 lg:gap-24 mb-10 md:mb-12">
          {/* Left: Title - matching FAQ layout */}
          <div className="mb-8 md:mb-0 md:w-[280px] lg:w-[320px] flex-shrink-0">
            <h2 className="text-[28px] md:text-[36px] leading-[1.2] font-sans font-normal text-neutral-950">
              {t("advisory.title")}
            </h2>
          </div>

          {/* Right: Description + CTA */}
          <div className="flex-1">
            <p className="mb-6 leading-relaxed font-sans text-base text-neutral-600">
              {t(descriptionKey)}
            </p>
            <button
              onClick={handleCtaClick}
              className="inline-flex items-center px-5 py-2.5 rounded-full bg-deep-blue-100 text-deep-blue-900 hover:bg-deep-blue-200 transition-colors text-sm font-medium"
            >
              {hasAdvisoryCall
                ? t("advisory.bookCall")
                : t("advisory.whatsappSupport")}
            </button>
          </div>
        </div>

        {/* Expert photos grid */}
        <div className="grid grid-cols-3 gap-3 md:flex md:flex-row md:gap-4">
          {displayedExperts.map((expert) => (
            <div key={expert.id} className="md:flex-1">
              {/* Photo */}
              <div className="aspect-[6/7] mb-3 overflow-hidden rounded-2xl bg-neutral-50">
                <img
                  src={expert.image}
                  alt={expert.name}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              {/* Name */}
              <p className="leading-[140%] font-sans font-medium text-base text-neutral-950">
                {expert.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdvisoryBoard;
